import React, { useCallback, useEffect, useRef, useState } from "react";
import "./style.scss";
import Search from "../../../assets/SearchMarket.svg";
import Filter from "../../../assets/Filter.svg";
import { TabBtn } from "../components/tab-btn";
import { MarketAsset } from "../components/market-asset";
import Button from "shared/ui/button";
import { SortModal } from "../components/sort-modal";
import { useStore } from "zustand";
import { marketStore } from "entities/market";
import { useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { AssetType, AssetTypes } from "static/types";
import { SortData } from "static/data";
import {
  isHeadUser,
  navigateToAsset,
  navigateToAssetByWill,
} from "helpers/functions";
import InfiniteScroll from "react-infinite-scroll-component";
import HorizontalScroll from "../components/horizontal-scroll";
import { useTranslation } from "react-i18next";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import SortConfig from "../components/sort";
import StrategyCard from "../components/strategy-card";
import PortfolioSection from "../components/portfolio-section";
import useSubAccount from "hooks/useSubAccount";
import ArrowUp from "../../../assets/ArrowUp.svg";
import { sessionModel } from "entities/session";

type PropsType = {
  currencies?: boolean;
  page: number;
  setPage: any;
};

const AVAILABLE_ASSET_TYPES: AssetType[] = [
  AssetType.ETF,
  AssetType.Stock,
  AssetType.Fond,
  AssetType.StructuredProduct,
  AssetType.Currency,
  AssetType.Bond,
  AssetType.EuroBond,
  AssetType.Option,
];

const SORTABLE_ASSET_TYPES: AssetType[] = [
  AssetType.ETF,
  AssetType.Stock,
  AssetType.Fond,
  AssetType.Bond,
  AssetType.EuroBond,
  AssetType.Option,
];

export const AssetsContainer: React.FC<PropsType> = ({
  currencies,
  page,
  setPage,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("app");
  const limit = 30;
  const currItemsLength = (page + 1) * limit;
  const {
    isLoading,
    activeAssetType,
    assets,
    assetsCount,
    assetsCurrencies,
    loadAssets,
    sortConfigs,
    strategies,
    marketScrollState,
    setMarketScrollState,
  } = useStore(marketStore);
  const activeTab = currencies ? AssetType.Currency : activeAssetType;
  const isStructuredProduct = activeTab === AssetType.StructuredProduct;
  const isAvailable = AVAILABLE_ASSET_TYPES.includes(activeTab);
  const restoredTabsRef = useRef<Record<number, boolean>>({});
  const isRestoringRef = useRef(false);
  const loadInFlightRef = useRef<string | null>(null);

  const onLoadAssets = useCallback(async () => {
    if (!isAvailable || isStructuredProduct) {
      return;
    }

    const isCurrencyTab = activeTab === AssetType.Currency;
    const currentAssets = isCurrencyTab ? assetsCurrencies : assets;

    const maxLoadedPage =
      currentAssets.length > 0
        ? Math.floor(currentAssets.length / limit) - 1
        : -1;

    // If we already have enough items for this page in the store,
    // reuse cache instead of hitting the API again.
    if (maxLoadedPage >= page) {
      return;
    }

    const loadKey = `${activeTab}-${page}`;
    if (loadInFlightRef.current === loadKey) {
      return;
    }
    loadInFlightRef.current = loadKey;

    const sortConfig =
      SORTABLE_ASSET_TYPES.includes(activeTab) && sortConfigs[activeTab];

    try {
      await loadAssets({
        type: String(activeTab),
        page,
        limit,
        ...(sortConfig && {
          sortBy: sortConfig.sortBy,
          orderBy: sortConfig.orderBy,
        }),
      });
    } finally {
      if (loadInFlightRef.current === loadKey) {
        loadInFlightRef.current = null;
      }
    }
  }, [
    activeTab,
    assets,
    assetsCurrencies,
    isAvailable,
    limit,
    loadAssets,
    page,
    sortConfigs,
  ]);

  const handleStrategy = (symbol: string) => {
    navigate(PATH_LIST.strategy(symbol), {
      state: { from: location.pathname },
    });
  };

  useEffect(() => {
    onLoadAssets();
  }, [onLoadAssets, page]);

  // restore scroll & page per tab from zustand
  useEffect(() => {
    const restoredForTab = restoredTabsRef.current[activeTab];
    const savedState = marketScrollState[activeTab];

    if (restoredForTab) {
      return;
    }

    // No saved state for this tab – normalize to first page & top
    if (!savedState) {
      setPage(0);
      restoredTabsRef.current[activeTab] = true;
      isRestoringRef.current = false;
      return;
    }

    // First, sync pagination with saved page
    if (savedState.page !== page) {
      isRestoringRef.current = true;
      setPage(savedState.page);
      return;
    }

    // When page already matches, restore scroll position in scrollable container
    const container = document.getElementById("market-scroll-wrapper");
    if (container) {
      isRestoringRef.current = true;
      container.scrollTop = savedState.scroll;
      requestAnimationFrame(() => {
        isRestoringRef.current = false;
      });
    } else {
      isRestoringRef.current = false;
    }
    restoredTabsRef.current[activeTab] = true;
  }, [activeTab, marketScrollState, page, setPage]);

  // track scroll changes and keep them in zustand
  useEffect(() => {
    const container = document.getElementById("market-scroll-wrapper");
    if (!container) return;

    const handleScroll = () => {
      if (isRestoringRef.current) {
        return;
      }

      const scrollTop = container.scrollTop;

      setMarketScrollState(Number(activeTab), {
        scroll: scrollTop,
        page,
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, page, setMarketScrollState]);

  if (assets.length === 0 && isLoading) {
    return <p className="assets-loading">{t("loading")}</p>;
  }
  if (!isAvailable) {
    return (
      <div className="not-found-coming-soon">
        <p>{t("comingSoon")}</p>
      </div>
    );
  }
  if (isStructuredProduct ? !strategies.length : !assets.length) {
    return (
      <div className="not-found-coming-soon">
        <p>{t("notFound")}</p>
      </div>
    );
  }
  if (isStructuredProduct) {
    return (
      <div className="strategy-market">
        {strategies.map(({ id, symbol, currency, data }, i) => (
          <StrategyCard
            id={i + 1}
            key={symbol}
            onClick={() => handleStrategy(id)}
            asset={strategies[i]}
            minPrice={""}
            risk={data.risk}
            percent={data.annual_yield}
          />
        ))}
      </div>
    );
  }

  return (
    <InfiniteScroll
      className="market-asset-list-wrapper"
      dataLength={currItemsLength}
      next={() => {
        setPage(page + 1);
      }}
      scrollableTarget="market-scroll-wrapper"
      hasMore={currItemsLength < assetsCount}
      scrollThreshold={0.9}
      loader={<p className="assets-loading">{t("loading")}</p>}
    >
      {assets.map((asset, index) => (
        <MarketAsset
          key={index}
          onClick={() => {
            const container = document.getElementById("market-scroll-wrapper");

            const scrollTop = container ? container.scrollTop : null;

            if (container) {
              setMarketScrollState(Number(activeTab), {
                scroll: scrollTop || 0,
                page,
              });
            }

            navigateToAsset(activeTab, asset.id, navigate);
          }}
          onMouseDown={(e) => {
            if (e.button === 1) {
              window.open(
                window.location.origin +
                  navigateToAssetByWill(activeTab, asset.id),
                "_blank",
              );
            }
          }}
          asset={asset}
        />
      ))}
    </InfiniteScroll>
  );
};

export const MarketPage = () => {
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [sortModal, setSortModal] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { t } = useTranslation("app");
  const [page, setPage] = useState(0);

  const [favoritesList, setFavoritesList] = useState<Record<number, any[]>>({});

  const { subAccount, isMarginSubAccount } = useSubAccount();
  const currentUser = sessionModel.useCurrentUser();

  useBlockScrollbar(sortModal, [sortModal]);

  const {
    isLoading,
    loadFavorites,
    favorites,
    strategies,
    activeAssetType: activeTab,
    sortConfigs: configs,
    setActiveAssetType,
    setSortConfigs,
    getStrategies,
  } = useStore(marketStore);

  const navigate = useNavigate();

  useEffect(() => {
    getStrategies();
    loadFavorites();
  }, []);

  // scroll to tabs current position
  useEffect(() => {
    const rightSideTabs = [6, 3, 8, 10];
    const tabBarId = document.getElementById("tab-bar-id");
    if (rightSideTabs.includes(activeTab) && tabBarId) {
      tabBarId.scrollTo({ left: tabBarId.scrollWidth, behavior: "smooth" });
    } else {
      tabBarId?.scrollTo({ left: 0, behavior: "smooth" });
    }
    console.log("activeTab", activeTab);
  }, [activeTab]);

  // toggle "scroll to top" button visibility based on scroll position
  useEffect(() => {
    const container = document.getElementById("market-scroll-wrapper");
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 400);
    };

    handleScroll();
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab]);

  const handleTabClick = (type: number) => {
    if (type != activeTab) {
      setActiveAssetType(type);
    }
  };

  const handleSearch = () => {
    navigate(PATH_LIST.search);
  };
  const handleFilter = () => {
    navigate(PATH_LIST.filter);
  };

  useEffect(() => {
    if (favorites.length) {
      setFavoritesList(() => {
        const list: Record<number, any[]> = {};
        for (const asset of favorites) {
          const type = asset.asset!.type;
          if (!(type in list)) {
            list[type] = [asset];
          } else {
            list[type].push(asset);
          }
        }
        return list;
      });
    }
  }, [favorites]);

  const Categories = [
    // { type: AssetType.Currency, label: "Currencies" },
    { type: 20, label: "Favorites" },
    ...(strategies.length > 0 && !isMarginSubAccount
      ? [{ type: AssetType.StructuredProduct, label: "strategies" }]
      : []),
    { type: AssetType.Stock, label: "Stocks" },
    { type: AssetType.ETF, label: "ETF" },
    ...(isMarginSubAccount
      ? []
      : [
          { type: AssetType.Bond, label: "Bonds" },
          { type: AssetType.EuroBond, label: "Eurobonds" },
        ]),
    ...(isMarginSubAccount
      ? [{ type: AssetType.Option, label: "options" }]
      : []),
  ];

  return (
    <div className="market">
      <div className="search-panel">
        <div onClick={handleSearch} className="serch-form">
          <img src={Search} alt="Search" />
          <span>{t("search")}</span>
        </div>
        <button onClick={handleFilter} className="filter-btn">
          <img src={Filter} alt="Filter" />
        </button>
      </div>

      <HorizontalScroll id="tab-bar-id" className="tab-bar">
        {Categories.map((cat) => (
          <TabBtn
            key={cat.type}
            text={t(cat.label)}
            active={activeTab === cat.type}
            onClick={() => {
              handleTabClick(cat.type);
            }}
          />
        ))}
      </HorizontalScroll>

      <div id="market-scroll-wrapper" className="market-main">
        {activeTab === 20 ? (
          favorites.length ? (
            Object.entries(favoritesList)
              .sort((a, b) => a[1].length - b[1].length)
              .map(([key, list]) => (
                <PortfolioSection
                  key={key}
                  favorites={list}
                  category={t(AssetTypes[+key as unknown as AssetType])}
                />
              ))
          ) : isLoading ? (
            <p className="assets-loading">{t("loading")}</p>
          ) : (
            <p className="no-favorites-text">{t("startSavingYourFavorites")}</p>
          )
        ) : (
          <AssetsContainer key={activeTab} page={page} setPage={setPage} />
        )}
      </div>

      {[
        AssetType.ETF,
        AssetType.Stock,
        AssetType.Fond,
        AssetType.Bond,
        AssetType.EuroBond,
        AssetType.Option,
      ].includes(activeTab) &&
        isButtonVisible && (
          <div className="btn-wrapper">
            {showScrollTop && (
              <button
                className="market-scroll-top-btn"
                onClick={() => {
                  const container = document.getElementById(
                    "market-scroll-wrapper",
                  );
                  if (container) {
                    container.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
              >
                <img src={ArrowUp} alt="ArrowUp" />
              </button>
            )}

            <Button
              onClick={() => setSortModal(true)}
              children={
                configs[activeTab]?.sortBy ? (
                  <SortConfig
                    name={t(
                      SortData[activeTab].find(
                        (a) => a.by === configs[activeTab].sortBy,
                      )?.text || "",
                    )}
                    config={
                      configs[activeTab].orderBy === "asc"
                        ? t("ascending")
                        : t("descending")
                    }
                    img={
                      configs[activeTab].orderBy === "asc"
                        ? "Ascending"
                        : "Descending"
                    }
                  />
                ) : (
                  t("sort")
                )
              }
              className={
                configs[activeTab]?.sortBy
                  ? "market-sort-btn sorted-configs"
                  : "market-sort-btn"
              }
              variant="dark"
            />
          </div>
        )}

      {sortModal && (
        <SortModal
          assetType={activeTab}
          sort={configs[activeTab]}
          onClose={() => setSortModal(false)}
          onSort={(v) => {
            setPage(0);
            setSortConfigs(activeTab, v);
          }}
        />
      )}
    </div>
  );
};
